import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Circle, FileBarChart, Mail, Play, Plug, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import AppShell from "../components/AppShell";
import { ErrorState, LoadingState } from "../components/LoadingState";
import api, { formatApiError } from "../lib/api";
import { CustomerSelect } from "../components/customer/CustomerUI";

const COMPLETE_SCAN_STATUSES = new Set(["completed", "completed_with_errors"]);

export default function Onboarding() {
  const navigate = useNavigate();
  const [connections, setConnections] = useState([]);
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [starting, setStarting] = useState(false);
  const [selectedConnectionId, setSelectedConnectionId] = useState("");

  const load = useCallback(async () => {
    setLoadError(false);
    setLoading(true);
    try {
      const [connectionsResponse, scansResponse] = await Promise.all([
        api.get("/connections"),
        api.get("/scans"),
      ]);
      setConnections(connectionsResponse.data);
      setSelectedConnectionId((previous) => connectionsResponse.data.some((item) => item.id === previous)
        ? previous : connectionsResponse.data.length === 1 ? connectionsResponse.data[0].id : "");
      setScans(scansResponse.data);
    } catch (error) {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const completedScan = useMemo(
    () => scans.find((scan) => COMPLETE_SCAN_STATUSES.has(scan.status)),
    [scans],
  );
  const automationReady = connections.some(
    (connection) => connection.schedule_enabled || connection.digest_per_scan || connection.digest_weekly,
  );
  const completedCount = Number(connections.length > 0) + Number(!!completedScan) + Number(automationReady);
  const progress = Math.round((completedCount / 3) * 100);

  const startScan = async () => {
    if (!connections.some((item) => item.id === selectedConnectionId)) return;
    setStarting(true);
    try {
      const { data } = await api.post(`/connections/${selectedConnectionId}/scan`);
      navigate(`/scans/${data.id}`);
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
      setStarting(false);
    }
  };

  if (loading) return <AppShell><LoadingState label="Preparing your CleanIQ workspace" /></AppShell>;
  if (loadError) return <AppShell><ErrorState message="We could not load your setup progress." onRetry={load} /></AppShell>;

  const steps = [
    {
      key: "connect",
      title: "Connect your CRM",
      description: "Add the CRM account CleanIQ will scan. Your credentials remain encrypted.",
      complete: connections.length > 0,
      icon: Plug,
      action: () => navigate("/connect?returnTo=/onboarding"),
      actionLabel: "Connect a CRM",
    },
    {
      key: "scan",
      title: "Run your first health scan",
      description: "CleanIQ reads your CRM and builds a scored, approval-ready health report.",
      complete: !!completedScan,
      icon: Play,
      action: startScan,
      actionLabel: starting ? "Starting scan…" : "Run first scan",
      disabled: !selectedConnectionId || starting,
    },
    {
      key: "automate",
      title: "Choose reports and automation",
      description: "Set scan frequency and email reports from your workspace. Manual scanning remains available.",
      complete: automationReady,
      icon: Mail,
      action: () => navigate("/dashboard"),
      actionLabel: "Configure workspace",
      disabled: connections.length === 0,
    },
  ];

  return <AppShell>
    <div data-testid="onboarding-page" className="mx-auto max-w-5xl">
      <section className="overflow-hidden rounded-3xl bg-gradient-to-br from-[#081426] via-[#0b1d38] to-blue-700 p-7 text-white shadow-2xl shadow-blue-950/15 sm:p-10">
        <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-cyan-200">Getting started</div>
        <div className="mt-3 flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="max-w-2xl font-heading text-3xl font-extrabold tracking-tight sm:text-4xl">
              Prepare your first CleanIQ health report
            </h1>
            <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-300">
              Complete these three steps to move from a new account to continuous CRM data-quality monitoring.
            </p>
          </div>
          <div className="shrink-0 text-left sm:text-right">
            <div className="font-mono text-3xl font-extrabold">{progress}%</div>
            <div className="text-xs text-slate-300">{completedCount} of 3 complete</div>
          </div>
        </div>
        <div className="mt-7 h-2 overflow-hidden rounded-full bg-white/15">
          <div data-testid="onboarding-progress" className="h-full rounded-full bg-cyan-300 transition-all" style={{ width: `${progress}%` }} />
        </div>
      </section>

      <div className="mt-6 space-y-4">
        {steps.map((step, index) => {
          const Icon = step.icon;
          return <section key={step.key} data-testid={`onboarding-step-${step.key}`}
            className="flex flex-col gap-5 rounded-2xl border border-slate-200 bg-white p-5 shadow-lg shadow-slate-900/5 sm:flex-row sm:items-center sm:p-6">
            <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full border ${
              step.complete ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-slate-50 text-slate-500"
            }`}>
              {step.complete ? <Check className="h-5 w-5" /> : <Icon className="h-5 w-5" />}
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-400">Step {index + 1}</div>
              <h2 className="mt-1 font-heading text-lg font-extrabold text-slate-950">{step.title}</h2>
              <p className="mt-1 text-sm leading-6 text-slate-600">{step.description}</p>
              {step.key === "scan" && !step.complete && connections.length > 0 && (
                <div className="mt-3">
                  <label htmlFor="onboarding-crm" className="mb-1 block text-sm font-medium text-slate-900">CRM to scan</label>
                  <CustomerSelect id="onboarding-crm" value={selectedConnectionId} disabled={starting}
                    onChange={(event) => setSelectedConnectionId(event.target.value)}>
                    <option value="" disabled>Choose a CRM</option>
                    {connections.map((connection) => <option key={connection.id} value={connection.id}>
                      {connection.label || connection.connector_name || "CRM"}
                    </option>)}
                  </CustomerSelect>
                </div>
              )}
            </div>
            {step.complete ? (
              <span className="inline-flex shrink-0 items-center gap-2 text-sm font-bold text-emerald-700">
                <Check className="h-4 w-4" /> Complete
              </span>
            ) : (
              <button onClick={step.action} disabled={step.disabled}
                className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl bg-slate-950 px-5 py-3 text-sm font-bold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-300">
                {starting && step.key === "scan" ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Circle className="h-4 w-4" />}
                {step.actionLabel}
              </button>
            )}
          </section>;
        })}
      </div>

      {completedScan && <section className="mt-6 flex flex-col gap-4 rounded-2xl border border-blue-200 bg-blue-50 p-5 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="font-bold text-blue-950">Your first health report is ready</div>
          <p className="mt-1 text-sm text-blue-800">Review findings before approving any CRM changes.</p>
        </div>
        <button onClick={() => navigate(`/scans/${completedScan.id}`)}
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-700 px-5 py-3 text-sm font-bold text-white hover:bg-blue-800">
          <FileBarChart className="h-4 w-4" /> Open health report
        </button>
      </section>}
    </div>
  </AppShell>;
}
